/// Tower Home structure ported from the iOS 2.8 redesign (levels_manifest.json
/// `collections` + `careerStages` + `liveLevels`, `TowerHomeView.collectionAccent`,
/// and `LevelFlag.map`). The flat web registry has none of this grouping, so it
/// lives here. Member ids that aren't in the web registry are filtered out at
/// render time (see TowerHome), so listing a not-yet-ported level is harmless.

export interface Shelf {
  id: string
  title: string
  /// Emoji stand-in for the iOS SF Symbol.
  icon: string
  /// Accent hex (mirrors collectionAccent()).
  accent: string
  levelIds: string[]
}

export interface CareerStage {
  id: string
  title: string
  /// Total wings needed to "unlock" the stage on iOS. On web we surface this as
  /// a progression badge, not a hard gate — playability stays on the existing
  /// free/FGU model so nothing that's free today becomes locked.
  wingsToUnlock: number
  levelIds: string[]
}

export const ACCENT = {
  mountain: '#2dd4bf', // teal
  mega: '#38bdf8', // sky
  islands: '#22d3ee', // cyan
  world: '#818cf8', // indigo
  carrier: '#7488a0', // steel blue
  career: '#facc15', // yellow
  live: '#22d3ee', // cyan
  daily: '#fb923c', // orange
  neutral: '#64748b', // slate
} as const

export const COLLECTIONS: Shelf[] = [
  { id: 'mountain_approaches', title: 'Mountain Approaches', icon: '⛰️', accent: ACCENT.mountain,
    levelIds: ['INN', 'VNLK', 'ZQN', 'KTEX', 'KGCN', 'AVL', 'PANC'] },
  { id: 'mega_hubs', title: 'Mega-Hubs', icon: '🏙️', accent: ACCENT.mega,
    levelIds: ['LAX', 'ORD', 'ATL', 'DXB', 'HKG', 'WSSS', 'HND', 'NRT', 'VABB', 'KIX'] },
  { id: 'islands_coast', title: 'Islands & Coast', icon: '🏝️', accent: ACCENT.islands,
    levelIds: ['HKO', 'CYVR', 'MCO', 'BCN', 'GRU', 'RIO', 'KEF'] },
  { id: 'world_tour', title: 'Around the World', icon: '🌍', accent: ACCENT.world,
    levelIds: ['MAD', 'FCO', 'ATH', 'JNB', 'IKA', 'KPHX', 'KDEN'] },
  { id: 'carrier_ops', title: 'Carrier Operations', icon: '⚓️', accent: ACCENT.carrier,
    levelIds: ['CARRIER'] },
]

export const CAREER_STAGES: CareerStage[] = [
  { id: 'SMALL_FIELDS', title: 'Small, quiet fields', wingsToUnlock: 0, levelIds: ['TUTORIAL', 'N51', 'TEB'] },
  { id: 'MID_SIZE', title: 'Busier airspaces', wingsToUnlock: 3, levelIds: ['SHD', 'TOL', 'SDL'] },
  { id: 'MILITARY_COMPLICATION', title: 'Class B & military', wingsToUnlock: 10, levelIds: ['SAN'] },
  { id: 'REGIONALS', title: 'Class B regionals', wingsToUnlock: 12, levelIds: ['BOS_MHT', 'EWR', 'NYC_HELI'] },
  { id: 'BIG_DOG_US', title: 'Washington area', wingsToUnlock: 18, levelIds: ['IAD'] },
  { id: 'GOING_INTERNATIONAL', title: 'Around the world', wingsToUnlock: 22, levelIds: ['SXM', 'SYD', 'MUN', 'EHAM'] },
  { id: 'FINAL_BOSSES', title: 'Complicated European airports', wingsToUnlock: 26, levelIds: ['LHR', 'CDG'] },
]

/// Web live-traffic level ids (iOS ships NYC/London/Atlanta; web has one so far).
export const LIVE_LEVEL_IDS = ['HANDOFF_NY', 'HANDOFF_NYC', 'HANDOFF_LONDON', 'HANDOFF_ATL']

/// id → ISO-3166 alpha-2 (ported from iOS LevelFlag.map). Drives the flag chip.
export const FLAG: Record<string, string> = {
  LAX: 'US', ORD: 'US', ATL: 'US', SAN: 'US', KDEN: 'US', KGCN: 'US', KTEX: 'US', KPHX: 'US',
  PANC: 'US', HKO: 'US', MCO: 'US', AVL: 'US', BOS_MHT: 'US', EWR: 'US', IAD: 'US', TEB: 'US',
  N51: 'US', SHD: 'US', TOL: 'US', SDL: 'US', NYC_HELI: 'US', CARRIER: 'US', TUTORIAL: 'US',
  HANDOFF_NY: 'US', HANDOFF_NYC: 'US', HANDOFF_ATL: 'US', HANDOFF_LONDON: 'GB',
  INN: 'AT', MUN: 'DE', ZQN: 'NZ', SYD: 'AU', VNLK: 'NP', VABB: 'IN', DXB: 'AE', IKA: 'IR',
  HKG: 'HK', WSSS: 'SG', KIX: 'JP', NRT: 'JP', HND: 'JP', MAD: 'ES', BCN: 'ES', FCO: 'IT',
  ATH: 'GR', JNB: 'ZA', CDG: 'FR', LHR: 'GB', EHAM: 'NL', CYVR: 'CA', GRU: 'BR', RIO: 'BR',
  KEF: 'IS', SXM: 'SX',
}

const LIVE_CODE: Record<string, string> = {
  HANDOFF_NY: 'NYC', HANDOFF_NYC: 'NYC', HANDOFF_LONDON: 'LON', HANDOFF_ATL: 'ATL',
}

/// The big tile glyph. Live → region code; else strip a leading K on 4-char US
/// ICAOs, drop a `_SUFFIX`, take the first 4 (mirrors LevelSigilView).
export function sigilCode(id: string): string {
  if (LIVE_CODE[id]) return LIVE_CODE[id]!
  let s = id.split('_')[0] ?? id
  if (/^K[A-Z]{3}$/.test(s)) s = s.slice(1)
  return s.slice(0, 4).toUpperCase()
}

/// ISO2 → regional-indicator emoji.
export function flagEmoji(id: string): string {
  const iso = FLAG[id]
  if (!iso) return ''
  return iso.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
}

/// Accent for a level: its collection's accent, else live/neutral.
const ACCENT_BY_LEVEL: Record<string, string> = (() => {
  const m: Record<string, string> = {}
  for (const c of COLLECTIONS) for (const id of c.levelIds) m[id] = c.accent
  for (const id of LIVE_LEVEL_IDS) m[id] = ACCENT.live
  return m
})()
export function accentForLevel(id: string): string {
  return ACCENT_BY_LEVEL[id] ?? ACCENT.neutral
}

export function isLiveLevel(id: string): boolean {
  return LIVE_LEVEL_IDS.includes(id)
}

/// Wings-gate on the career stages — mirrors iOS (a stage locks until you've
/// earned enough wings). Flip to false to fall back to a pure display badge so
/// nothing gets locked beyond the existing free/FGU model.
export const CAREER_WINGS_GATE = true

/// Daily-challenge pick: deterministic by UTC day from the playable pool, so the
/// same helper drives both the home card and the streak hook in the store.
export function dailyDayIndex(): number {
  return Math.floor(Date.now() / 86_400_000)
}
export function isoDayString(dayIndex: number): string {
  return new Date(dayIndex * 86_400_000).toISOString().slice(0, 10)
}
export function dailyLevelId(dayIndex: number, poolIds: string[]): string | null {
  const pool = poolIds.filter((id) => id !== 'TUTORIAL')
  if (!pool.length) return null
  const i = ((dayIndex % pool.length) + pool.length) % pool.length
  return pool[i]!
}

/// Streak bookkeeping for the daily challenge. `lastDate` is a UTC `YYYY-MM-DD`
/// string (matching the day index that picks the daily level). Lives here (not
/// userProfile) so it stays firebase-free and unit-testable.
export interface DailyChallengeState {
  lastDate: string
  streak: number
  best: number
  /// Best score on the daily for `lastDate` (today) + the difficulty that
  /// achieved it — what the day-scoped leaderboard publishes.
  lastScore?: number
  lastDifficulty?: string
}

/// The leaderboards-collection levelId for a given day's daily board. Lives
/// under the existing `leaderboards/{levelId}/...` schema, so the existing read
/// rule covers it and no rule change is needed. Single board per day (all
/// difficulties together) under the `all` segment; difficulty is shown per row.
export function dailyBoardLevelId(dateStr: string): string {
  return `DAILY_${dateStr}`
}
export const DAILY_BOARD_SEGMENT = 'all'

/// Pure streak transition: same UTC day → unchanged (same object); consecutive
/// day → +1; any gap → reset to 1. `best` tracks the longest run.
export function nextDailyState(
  prev: DailyChallengeState | undefined,
  today: string,
  yesterday: string,
): DailyChallengeState {
  if (prev?.lastDate === today) return prev
  const streak = prev?.lastDate === yesterday ? prev.streak + 1 : 1
  return { lastDate: today, streak, best: Math.max(prev?.best ?? 0, streak) }
}
